// Shared version utility functions

interface VersionInfo {
  version: string;
  environment: string;
  gitCommit?: string;
  buildDate?: string;
  deploymentDate?: string;
}

interface BackendVersionInfo {
  version: string;
  environment: string;
  gitCommit?: string;
  buildDate?: string;
  deploymentDate?: string;
  error?: string;
}

// Get version from version.json for local development
const getLocalVersion = (): string => {
  try {
    // This will only work in development, in production it will be undefined
    const versionData = require('../version.json');
    return versionData.version || '1.0.0-staging';
  } catch {
    return '1.0.0-staging';
  }
};

const getLocalEnvironment = (): string => {
  // If we're in development (no environment variables set), show 'development'
  if (!process.env.REACT_APP_ENVIRONMENT && !process.env.REACT_APP_VERSION) {
    return 'development';
  }
  
  // If environment variable is set, use it
  if (process.env.REACT_APP_ENVIRONMENT) {
    return process.env.REACT_APP_ENVIRONMENT;
  }
  
  // Otherwise, try to read from version.json
  try {
    const versionData = require('../version.json');
    return versionData.environment || 'staging';
  } catch {
    return 'staging';
  }
};

export const getVersionInfo = (): VersionInfo => {
  return {
    version: process.env.REACT_APP_VERSION || getLocalVersion(),
    environment: process.env.REACT_APP_ENVIRONMENT || getLocalEnvironment(),
    gitCommit: process.env.REACT_APP_GIT_COMMIT || 'local',
    buildDate: process.env.REACT_APP_BUILD_DATE || new Date().toISOString(),
    deploymentDate: process.env.REACT_APP_BUILD_DATE || new Date().toISOString(),
  };
};

// Backend version functions
let backendVersionCache: BackendVersionInfo | null = null;
let backendVersionCacheTime = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export const getBackendVersionInfo = async (): Promise<BackendVersionInfo> => {
  const now = Date.now();
  
  // Return cached version if still valid
  if (backendVersionCache && (now - backendVersionCacheTime) < CACHE_DURATION) {
    return backendVersionCache;
  }
  
  try {
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
    const response = await fetch(`${apiUrl}/version/`);
    
    if (response.ok) {
      const data = await response.json();
      backendVersionCache = data;
      backendVersionCacheTime = now;
      return data;
    } else {
      throw new Error(`HTTP ${response.status}`);
    }
  } catch (error) {
    // Return cached version if available, otherwise return error info
    if (backendVersionCache) {
      return backendVersionCache;
    }
    
    return {
      version: 'unknown',
      environment: 'unknown',
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}; 